import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { employerSignUp, applicantSignUp, adminSignIn } from '../apiconfig/api';

interface User {
  name: string;
  companyName: string;
  phoneNo: string;
}

interface Applicant {
  uuid?: string;
  name: string;
  phoneNo: string;
  phoneNumber: string;
  zipCode: string;
  videoUuid?: string | null;
  resumeDocumentId?: string | null;
  submittedToEmployerId: string;
  submittedToEmployerPositionId: string;
  
  data?: {
    user?: {
      uuid: string;
      phoneNo: string;
      fullName: string;
      verified: boolean;
      applicants: Array<{
        uuid: string;
        zipCode: string;
      }>;
    };
    applicant?: {
      uuid: string;
      userId: string;
      position: any;
      videoUuid: string | null;
      applicationStatus: string;
      superApplied: boolean;
      zipCode: string;
      rating: number | null;
      submittedToEmployerId: string;
      submittedToEmployerPositionId: string;
      resumeDocumentId: string | null;
      createdAt: string;
      updatedAt: string;
      deletedAt: string | null;
      user: {
        uuid: string;
        role: string;
        fullName: string;
        email: string | null;
        password: string | null;
        phoneNo: string;
        verified: boolean;
        isSystemAdmin: boolean;
        createdAt: string;
        updatedAt: string;
        deletedAt: string | null;
      };
      submittedToEmployerPosition: any;
      resume: any;
    };
  }
}

interface Admin {
  email: string;
  password: string;
}

interface AdminUser {
  uuid: string;
  role: string;
  fullName: string;
  email: string;
  password: string;
  phoneNo: string | null;
  verified: boolean;
  createdAt: string;
  updatedAt: string;
  deletedAt: string | null;
}

interface AdminResponseData {
  token: string;
  user: AdminUser;
}

interface AdminResponse {
  data: AdminResponseData;
}

interface AuthState {
  user: User | null;
  admin: Admin | null;
  applicant: Applicant | null;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
  loading: boolean;
}

const initialState: AuthState = {
  user: null,
  applicant: null,
  admin: null,
  status: 'idle',
  error: null,
  loading: false,
};

// Employer signup thunk
export const employSignUp = createAsyncThunk<User, User, { rejectValue: string }>(
  'auth/signupEmploy',
  async ({ name, companyName, phoneNo }, { rejectWithValue }) => {
    try {
      const response = await employerSignUp(name, companyName, phoneNo);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);

// Person signup thunk
export const applicantSignUpThunk = createAsyncThunk<Applicant, Applicant, { rejectValue: string }>(
  'auth/signupApplicant',
  async (payload, { rejectWithValue }) => {
    try {
      const response = await applicantSignUp(payload);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);


export const adminSignInThunk = createAsyncThunk<AdminResponse, Admin,{ rejectValue: string }>(
  'auth/adminSignIn',
  async ({ email, password }, { rejectWithValue }) => {
    try {
      const response = await adminSignIn(email, password);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.message);
    }
  }
);




const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.admin = null;
      state.status = 'idle';
      state.error = null;
      state.loading = false;
    },
    clearApplicant: (state) => {
      state.applicant = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Employer
      .addCase(employSignUp.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(employSignUp.fulfilled, (state, action: PayloadAction<User>) => {
        state.status = 'succeeded';
        state.user = action.payload;
        state.loading = false;
      })
      .addCase(employSignUp.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'An error occurred';
        state.loading = false;
      })

      // Person
      .addCase(applicantSignUpThunk.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(applicantSignUpThunk.fulfilled, (state, action: PayloadAction<Applicant>) => {
        state.status = 'succeeded';
        state.applicant = action.payload;
        state.loading = false;
      })
      .addCase(applicantSignUpThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'An error occurred';
        state.loading = false;
      })

      // Admin
       .addCase(adminSignInThunk.pending, (state) => {
        state.status = 'loading';
        state.loading = true;
      })
      .addCase(adminSignInThunk.fulfilled, (state, action: PayloadAction<AdminResponse>) => {
        state.status = 'succeeded';
        state.admin = action.payload.data.user;
        state.loading = false;
      })
      .addCase(adminSignInThunk.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.payload ?? 'An error occurred';
        state.loading = false;
      })

  },
});

export const { logout, clearApplicant } = authSlice.actions;
export default authSlice.reducer;
